import { motion } from 'motion/react';
import { Instagram, Twitter, Smartphone, Mail, MapPin, Sparkles, ArrowRight, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';

const footerLinks = {
  producto: [
    { name: 'Funcionalidades', href: '#features' },
    { name: 'Precios', href: '#pricing' },
    { name: 'Cómo funciona', href: '#how-it-works' },
    { name: 'Comparación', href: '#comparison' },
  ],
  soporte: [
    { name: 'Centro de ayuda', href: '#' },
    { name: 'Contacto', href: '#' },
    { name: 'FAQ', href: '#' },
    { name: 'Comunidad', href: '#' },
  ],
  legal: [
    { name: 'Privacidad', href: '/privacy' },
    { name: 'Términos', href: '/terms' },
  ],
};

const socialLinks = [
  { name: 'Instagram', icon: Instagram, href: '#' },
  { name: 'Twitter', icon: Twitter, href: '#' },
];

const countries = ['🇲🇽', '🇨🇴', '🇦🇷', '🇧🇷', '🇵🇪', '🇨🇱'];

export function Footer() {
  return (
    <footer className="relative overflow-hidden">
      {/* Top gradient line */}
      <div className="h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-muted/30 to-muted/60" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-4">
            <div className="flex items-center gap-2.5 mb-6">
              <img src="/logo.png" alt="Frappi" className="w-10 h-10 object-contain" />
              <span className="text-2xl font-bold text-foreground tracking-tight">Frappi</span>
            </div>

            <p className="text-muted-foreground mb-6 max-w-xs leading-relaxed">
              Tu asistente personal de estudios potenciado con IA. Organiza tareas, mejora calificaciones y alcanza el éxito académico.
            </p>

            {/* Price highlight */}
            <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/60 backdrop-blur-sm border border-primary/15 mb-6 shadow-sm">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm">
                Desde <span className="font-bold text-primary">$2.99</span>/mes
              </span>
            </div>

            {/* Download button */}
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button className="w-full sm:w-auto bg-gradient-to-r from-[#a67eec] to-[#8b6de0] hover:from-[#9568d9] hover:to-[#7d5cc8] mb-6 group shadow-lg shadow-primary/20 font-semibold">
                <Smartphone size={18} className="mr-2" />
                Descargar en Google Play
                <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>

            {/* Countries */}
            <div className="mb-6">
              <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5 font-medium">
                <MapPin size={12} />
                Disponible en LATAM
              </p>
              <div className="flex gap-1.5">
                {countries.map((flag, index) => (
                  <motion.span
                    key={index}
                    whileHover={{ scale: 1.2, y: -2 }}
                    className="text-xl cursor-default"
                  >
                    {flag}
                  </motion.span>
                ))}
              </div>
            </div>

            {/* Social links */}
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.name}
                  href={social.href}
                  whileHover={{ scale: 1.1, y: -2 }}
                  className="w-10 h-10 rounded-xl bg-white/60 hover:bg-primary/10 border border-border/50 hover:border-primary/20 flex items-center justify-center text-muted-foreground hover:text-primary transition-all duration-200 shadow-sm"
                  aria-label={social.name}
                >
                  <social.icon size={18} />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Links */}
          <div className="md:col-span-2">
            <h3 className="font-bold text-foreground mb-5 text-sm uppercase tracking-wider">Producto</h3>
            <ul className="space-y-3">
              {footerLinks.producto.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <h3 className="font-bold text-foreground mb-5 text-sm uppercase tracking-wider">Soporte</h3>
            <ul className="space-y-3">
              {footerLinks.soporte.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <h3 className="font-bold text-foreground mb-5 text-sm uppercase tracking-wider">Legal</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="col-span-2 md:col-span-2">
            <h3 className="font-bold text-foreground mb-5 text-sm uppercase tracking-wider">Contacto</h3>
            <a
              href="mailto:hola@frappi.app"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-4"
            >
              <Mail size={16} />
              hola@frappi.app
            </a>

            <div className="mt-6 p-4 rounded-xl bg-white/60 backdrop-blur-sm border border-border/50 shadow-sm">
              <p className="text-xs text-muted-foreground mb-2 font-medium">Planes desde</p>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-extrabold bg-gradient-to-r from-[#a67eec] to-[#8b6de0] bg-clip-text text-transparent">$2.99</span>
                <span className="text-sm text-muted-foreground">/mes</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">7 días gratis · Todo incluido</p>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-8 border-t border-border/50">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              &copy; {new Date().getFullYear()} Frappi. Todos los derechos reservados.
            </p>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Hecho con</span>
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="text-red-500 inline-block"
              >
                <Heart size={14} className="fill-red-500" />
              </motion.span>
              <span>para estudiantes de LATAM</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
